import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PatientContext,
  PatientDispatchContext,
} from "../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../contexts/ProjectContext.tsx";
import { useFetch } from "../../services/hooks/useFetch.ts";
import { apiPrefix } from "../../utils/main.ts";
import CodeList from "../codelist/CodeList.tsx";
import VisitsDiagnosesProceduresSkeleton from "./skeletons/VisitsDiagnosesProceduresSkeleton.js";
import Visits from "./visits/Visits.tsx";

export default function VisitsDiagnosesProcedures() {
  const patient = useContext(PatientContext);
  const dispatch = useContext(PatientDispatchContext);

  const { highlightDate } = useContext(ProjectContext)!;
  const [tabSelected, setTabSelected] = useState(0);

  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");

  const {
    data: encounters,
    isLoading: encountersLoading,
    fetch: fetchEncounters,
    reset: resetEncounters,
  } = useFetch<PatientEncounterSummary[]>();

  const {
    data: diagnosisCodes,
    isLoading: diagnosisCodesLoading,
    fetch: fetchDiagnosisCodes,
    reset: resetDiagnosisCodes,
  } = useFetch<DiagnosisCodeSummary[]>();

  const {
    data: procedureCodes,
    isLoading: procedureCodesLoading,
    fetch: fetchProcedureCodes,
    reset: resetProcedureCodes,
  } = useFetch<ProcedureCodeSummary[]>();

  function handleTabChange(
    _: React.SyntheticEvent<Element, Event>,
    value: number,
  ) {
    setTabSelected(value);
  }

  function TabTitle({
    title,
    count,
    newCount = 0,
  }: {
    title: string;
    count?: number;
    newCount?: number;
  }) {
    const countChip =
      count !== undefined ? (
        <Chip label={count} size="small" />
      ) : (
        <CircularProgress size={20} />
      );
    const newCountChip = !!highlightDate && (
      <Chip
        label={newCount + " new"}
        size="small"
        sx={{ backgroundColor: "highlight.main", textTransform: "none" }}
      />
    );
    if (newCountChip) {
      // We want to allow line breaks if there are multiple chips
      return (
        <span>
          <span className="tab-title">{title}</span>
          <span>
            {countChip} {newCountChip}
          </span>
        </span>
      );
    } else {
      return (
        <span className="tab-title">
          {title} {countChip}
        </span>
      );
    }
  }

  useEffect(() => {
    if (!patient || !patient.id || !projectId) {
      resetEncounters();
      resetDiagnosisCodes();
      resetProcedureCodes();
      return;
    }
    if (!encounters && !encountersLoading) {
      void fetchEncounters(
        `${apiPrefix}/projects/${projectId}/patients/${patient.id}/encounters/`,
        {
          postResponseAction: (data) => {
            dispatch({ type: "update", patient: { encounters: data } });
          },
        },
      );
    }
  }, [
    patient,
    projectId,
    fetchEncounters,
    tabSelected,
    encounters,
    encountersLoading,
    dispatch,
    resetEncounters,
    resetDiagnosisCodes,
    resetProcedureCodes,
  ]);

  useEffect(() => {
    if (!patient || !patient.id || !projectId) {
      return;
    }
    if (tabSelected === 1 && !diagnosisCodes && !diagnosisCodesLoading) {
      void fetchDiagnosisCodes(
        `${apiPrefix}/projects/${projectId}/patients/${patient.id}/diagnosisCodes`,
      );
    }
  }, [
    patient,
    projectId,
    tabSelected,
    fetchDiagnosisCodes,
    diagnosisCodes,
    diagnosisCodesLoading,
  ]);

  useEffect(() => {
    if (!patient || !patient.id || !projectId) {
      return;
    }
    if (tabSelected === 2 && !procedureCodes && !procedureCodesLoading) {
      void fetchProcedureCodes(
        `${apiPrefix}/projects/${projectId}/patients/${patient.id}/procedureCodes`,
      );
    }
  }, [
    patient,
    projectId,
    tabSelected,
    fetchProcedureCodes,
    procedureCodes,
    procedureCodesLoading,
  ]);

  if (
    !patient ||
    !patient.id ||
    !patient.encounters ||
    !patient.encounters.length
  ) {
    return <VisitsDiagnosesProceduresSkeleton />;
  }

  return (
    <div className="visitsDiagnosesProcedures">
      <Card>
        <CardHeader
          title="Visits, Diagnoses and Procedures"
          slotProps={{
            title: {
              align: "left",
              className: "card-title",
              variant: "body1",
            },
          }}
        />
        <CardContent>
          <Tabs
            className={"tabs"}
            value={tabSelected}
            onChange={handleTabChange}
            centered
          >
            <Tab
              className="tab"
              label={
                <TabTitle
                  title={"All Visits"}
                  count={patient.encounterCount?.total}
                  newCount={patient.encounterCount?.highlight}
                />
              }
            />
            <Tab
              className="tab"
              label={
                <TabTitle
                  title={"All Diagnoses"}
                  count={patient.diagnosisCodeCount?.total}
                  newCount={patient.diagnosisCodeCount?.highlight}
                />
              }
            />
            <Tab
              className="tab"
              label={
                <TabTitle
                  title={"All Procedures"}
                  count={patient.procedureCodeCount?.total}
                  newCount={patient.procedureCodeCount?.highlight}
                />
              }
            />
          </Tabs>
        </CardContent>
        <CardContent>
          {tabSelected === 0 && <Visits loading={encountersLoading} />}
          {tabSelected === 1 && (
            <CodeList loading={diagnosisCodesLoading} codes={diagnosisCodes} />
          )}
          {tabSelected === 2 && (
            <CodeList loading={procedureCodesLoading} codes={procedureCodes} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
